/**
 * A deity's entity fields — `/v1/admin/taxonomy/deities/{id}/entity` (CSR-124): its parent, aliases,
 * Wikipedia title, editorial significance and whether it earns a page. Demand is read-only; the
 * re-measure job stamps it.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type DeityEntity = components['schemas']['DeityEntityRow'];
export type DeityEntityPatch = components['schemas']['DeityEntityPatchIn'];

export function useDeityEntity(id: number) {
  return useQuery({
    queryKey: keys.deityEntity(id),
    queryFn: () => authedClient.request<DeityEntity>(`/v1/admin/taxonomy/deities/${id}/entity`),
  });
}

/** Only the fields sent change; `parent_deity_id: null` detaches a deity from its parent. */
export function usePatchDeityEntity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: DeityEntityPatch }) =>
      authedClient.request<DeityEntity>(`/v1/admin/taxonomy/deities/${id}/entity`, { method: 'PATCH', body: patch }),
    onSuccess: (row) => queryClient.setQueryData(keys.deityEntity(row.id), row),
  });
}
